import { inject, injectable } from "@launchtray/tsyringe-async";
import { IProfileController } from "./IProfileController";
import { IGuestModel } from "../../../../common/models/IGuestModel";
import { IUserLevelUpModel } from "../../../userlevelup/model/IUserLevelUpModel";
import { UserLevelUpPopupScene } from "../../../userlevelup/UserLevelUpPopupScene";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { IPlayerModel } from "../../../../common/models/IPlayerModel";
import { PlayerDto } from "by/gamefactory/cityversetycoon/networking/data/responses/user/PlayerDto";
import { IContextResolver } from "by/gamefactory/cityversetycoon/composition/IContextResolver";
import { ShopModule } from "../../../shop/ShopModule";
import { GitBookPage, Redirector } from "../../../../../../tools/Redirector";
import { Web3Model } from "../../../../../../web3/models/Web3Model";
import { SignOutPopupScene } from "../../signout/SignOutPopupScene";
import { AsyncOperationNotification } from "../../../../common/components/AsyncOperationNotification";

@injectable()
export class ProfileController implements IProfileController {
    private readonly guestModel: IGuestModel;
    private readonly playerModel: IPlayerModel;
    private readonly web3Model: Web3Model;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly sceneManager: SceneManager;
    private readonly userLevelPopupSceneFactory: (userLevelUpModel: IUserLevelUpModel) => Promise<UserLevelUpPopupScene>;
    private readonly contextResolver: IContextResolver;

    public constructor(sceneManager: SceneManager,
        @inject("IGuestModel") guestModel: IGuestModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        web3Model: Web3Model,
        @inject("UserLevelUpPopupSceneFactory") userLevelPopupSceneFactory: (userLevelUpModel: IUserLevelUpModel) => Promise<UserLevelUpPopupScene>,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("IContextResolver") contextResolver: IContextResolver
    ) {
        this.sceneManager = sceneManager;
        this.guestModel = guestModel;
        this.playerModel = playerModel;
        this.web3Model = web3Model;
        this.userLevelPopupSceneFactory = userLevelPopupSceneFactory;
        this.asyncOperationNotification = asyncOperationNotification;
        this.contextResolver = contextResolver;
    }

    public async changeOnNextAvatar(): Promise<void> {
        const avatarsCount = this.guestModel.avatarUrls.value.length;
        const currentIndex = this.guestModel.avatarUrls.value.findIndex(url => url === this.guestModel.currentAvatarUrl.value);
        let nextAvatarIndex = currentIndex + 1;
        if (nextAvatarIndex >= avatarsCount) {
            nextAvatarIndex = 0;
        }
        await this.asyncOperationNotification.autoShowHide(this.guestModel.switchAvatar(nextAvatarIndex));
    }

    public async changeOnLastAvatar(): Promise<void> {
        const avatarsCount = this.guestModel.avatarUrls.value.length;
        const currentIndex = this.guestModel.avatarUrls.value.findIndex(url => url === this.guestModel.currentAvatarUrl.value);
        let nextAvatarIndex = currentIndex - 1;
        if (nextAvatarIndex < 0) {
            nextAvatarIndex = avatarsCount - 1;
        }
        await this.asyncOperationNotification.autoShowHide(this.guestModel.switchAvatar(nextAvatarIndex));
    }

    public async changeName(name: string): Promise<void> {
        if (name.length < 3) {
            return;
        }
        this.guestModel.changeGuestName(name);
        await this.guestModel.saveGuestProfile();
    }

    public async saveGuestProfile(): Promise<void> {
        await this.guestModel.saveGuestProfile();
    }

    public async showUserLevelUpPopup(model: IUserLevelUpModel): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.userLevelPopupSceneFactory(model));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async updatePlayer(playerDto: PlayerDto): Promise<void> {
        this.playerModel.updateValuesFromUpgrade(playerDto);
    }

    public async showShopPopup(): Promise<void> {
        const shopModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(ShopModule));
        await shopModule.start();
    }

    public async showSignOutPopup(): Promise<boolean> {
        return await this.sceneManager.showViewableAsync(await this.contextResolver.resolve(SignOutPopupScene));
    }

    public openGitbook(page?: GitBookPage): void {
        Redirector.openGitBook(page);
    }

    public openIntercom(): void {
        Redirector.openIntercom(this.web3Model.email, Date.now(), this.guestModel.name.value, this.guestModel.id.value);
    }
}
